import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { notificationsAPI } from '../services/api';
import { Plus, Edit2, Trash2, Bell } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';
import ConfirmationModal from '../components/ConfirmationModal';

const Notifications = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToCreate, goToEdit } = createNavigationHelpers('notifications', navigate);
  const { notifications: notificationPerms } = usePermissions();

  // Fetch notifications
  const { data: notificationsData, isLoading, error } = useQuery(
    ['notifications'],
    () => notificationsAPI.getAll(),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Notifications query success:', data);
      },
      onError: (error) => {
        console.error('Notifications fetch error:', error);
        toast.error('Failed to load notifications');
      }
    }
  );



  // Delete notification mutation
  const deleteMutation = useMutation(
    (uuid) => notificationsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        toast.success('Notification deleted successfully!');
        setDeleteModalOpen(false);
        setNotificationToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleDelete = (notification) => {
    setNotificationToDelete(notification);
    setDeleteModalOpen(true);
  };



  const handleConfirmDelete = () => {
    if (notificationToDelete) {
      deleteMutation.mutate(notificationToDelete.uuid);
    }
  };


  // Debug logging
  console.log('=== NOTIFICATIONS DEBUG ===');
  console.log('Raw notificationsData:', notificationsData);
  console.log('notificationsData?.data:', notificationsData?.data);
  console.log('notificationsData?.data?.data:', notificationsData?.data?.data);
  
  // Extract notifications from the correct path in the API response
  let notifications = [];
  
  // First check if we have the nested structure (data.data.data)
  if (Array.isArray(notificationsData?.data?.data)) {
    notifications = notificationsData.data.data;
    console.log('Using nested data structure for notifications');
  } 
  // Then check if we have a simpler structure (data.data)
  else if (Array.isArray(notificationsData?.data)) {
    notifications = notificationsData.data;
    console.log('Using simple data structure for notifications');
  }
  // Check if data is in the pagination format
  else if (notificationsData?.data?.data && Array.isArray(notificationsData.data.data)) {
    notifications = notificationsData.data.data;
    console.log('Using pagination data structure for notifications');
  }
  
  console.log('Extracted notifications:', notifications ? notifications.length : 0);
  
  // Ensure notifications is always an array
  notifications = notifications || [];
  
  // Simple utility functions for notification display
  const getTypeColor = (type) => 'bg-gray-100';
  const getTypeIcon = () => <span>•</span>;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load notifications</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Notifications</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage system notifications and alerts
            </p>
          </div>
          {/* Add Notification Button - Only show if user can create notifications */}
          {notificationPerms.canCreate && (
            <button
              onClick={goToCreate}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Notification
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.map((notification, index) => (
          <div
            key={notification.uuid}
            className="card-modern group hover-lift animate-fade-in-up overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div className="flex items-start space-x-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-glow flex-shrink-0 group-hover:scale-110 transition-transform duration-300 ${
                  notification.target_audience === 'employees' 
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-500' 
                    : 'bg-gradient-to-br from-green-500 to-emerald-500'
                }`}>
                  {notification.target_audience === 'employees' ? '👥' : '👤'}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white break-words">{notification.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base line-clamp-3 break-words">{notification.message}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400`}>
                  {notification.type}
                </span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span className="truncate">
                Target: {notification.target_audience === 'employees' ? '👥 All Employees' : '👤 All Customers'}
              </span>
              <span className="truncate">Created: {new Date(notification.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Edit Button - Only show if user can update notifications */}
              {notificationPerms.canUpdate && (
                <button
                  onClick={() => goToEdit(notification.uuid)}
                  className="btn-secondary text-sm flex items-center gap-1 px-3 py-2 rounded-lg"
                >
                  <Edit2 className="h-3 w-3 flex-shrink-0" />
                  Edit
                </button>
              )}
              
              {/* Delete Button - Only show if user can delete notifications */}
              {notificationPerms.canDelete && (
                <button
                  onClick={() => handleDelete(notification)}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                >
                  <Trash2 className="h-3 w-3 flex-shrink-0" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {notifications.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.19 4.19A2 2 0 006 3h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No notifications yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Get started by creating your first notification</p>

        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setNotificationToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Notification"
        message={`Are you sure you want to delete the notification "${notificationToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Notifications; 